import { DragIndicator } from "@mui/icons-material"
import { Backdrop, Box, Drawer } from "@mui/material"
import useResizeHandler from "hooks/resize-handler"
import { clamp } from "lodash"
import PropTypes from "prop-types"
import { useState } from "react"
import "./style.scss"

const DEFAULT_MIN_WIDTH_PERCENT = 25
const DEFAULT_MAX_WIDTH_PERCENT = 50

/**
 * Get the width in percentage of the viewport
 * @param {number} width - Width in pixels
 * @param {Object} opts - Options
 * @param {boolean} [opts.round=false] - If "true", the width will be rounded to the nearest integer
 * @param {boolean} [opts.numeric=false] - If "true", the width will be returned as a number, otherwise a string with a % will be returned
 * @returns {number | string} - Width in percentage of the viewport
 */
function getWidthPercent(width, opts = { round: false, numeric: false }) {
  const viewportWidth = window.innerWidth
  if (viewportWidth <= 0) {
    return 0
  }

  let widthPercent = width / viewportWidth * 100
  if (opts.round) {
    widthPercent = Math.round(widthPercent)
  }
  if (opts.numeric) {
    return widthPercent
  }
  return `${widthPercent}%`
}

function getValidWidth(width, { minWidthPercent = DEFAULT_MIN_WIDTH_PERCENT, maxWidthPercent = DEFAULT_MAX_WIDTH_PERCENT } = {}) {
  if (!width || (typeof width === "string" && !width.endsWith("%"))) {
    return `${minWidthPercent}%`
  }

  let widthPercent = 0
  if (typeof width === "number") {
    widthPercent = getWidthPercent(width, { numeric: true })
  } else {
    widthPercent = parseFloat(width)
  }

  return `${clamp(widthPercent, minWidthPercent, maxWidthPercent)}%`
}

/**
 * Resizable Drawer
 * @param {Object} props
 * @param {number | string} props.initialWidth - Initial width of the drawer in pixels (number) or percentage (string with a %)
 * @param {React.ReactNode} props.children - Children of the drawer
 * @param {"left" | "right" | "top" | "bottom"} props.anchor - Side from which the drawer will appear
 * @param {boolean} props.open - If "true", the drawer is open
 * @param {function} props.onClose - Callback when the drawer is closed
 * @param {boolean} props.backdrop - If "true", a backdrop will be shown when the drawer is open
 * @param {number} props.minWidthPercent - Minimum width of the drawer in the viewport
 * @param {number} props.maxWidthPercent - Maximum width of the drawer in the viewport
 * @param {function} props.onResize - Callback when the drawer is resized, receives the width in percentage of the viewport (string with a %)
 * @param {boolean} props.closeOnBackdropClick - If "true", the drawer will be closed when the backdrop is clicked
 * @param {function} props.onDraggingChange - Callback when the dragging state changes
 */
function ResizableDrawer({
  initialWidth,
  children,
  anchor = "right",
  open,
  onClose,
  backdrop = true,
  minWidthPercent,
  maxWidthPercent,
  onResize,
  closeOnBackdropClick,
  onDraggingChange,
}) {
  const [isDragging, setIsDragging] = useState(false)
  const [width, setWidth] = useState(getValidWidth(initialWidth, { minWidthPercent, maxWidthPercent }))

  function handleSetIsDragging(isDragging) {
    setIsDragging(isDragging)
    if (onDraggingChange) {
      onDraggingChange(isDragging)
    }
  }

  function handleDragEnd(width) {
    if (onResize) {
      onResize(getWidthPercent(width, { round: true }))
    }
  }

  function handleResize(width) {
    setWidth(getWidthPercent(width))
  }

  const { handleMouseDown: handleResizerMouseDown } = useResizeHandler({
    isDragging,
    setIsDragging: handleSetIsDragging,
    setContentWidth: handleResize,
    onDragEnd: handleDragEnd,
    minWidthPercent,
    maxWidthPercent,
  })

  function handleBackdropClick() {
    if (closeOnBackdropClick) {
      onClose()
    }
  }

  return <div className="components_material_resizable-drawer">
    <Drawer
      variant="persistent"
      anchor={anchor}
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          className: "drawer-container",
          sx: { width },
          elevation: 10,
        },
      }}
    >
      <Box className="resize-handle" onMouseDown={handleResizerMouseDown} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <DragIndicator fontSize="small" />
      </Box>
      <Box className="contents" elevation={5}>
        {children}
      </Box>
    </Drawer>
    {backdrop && <Backdrop open={open} sx={{ zIndex: (theme) => theme.zIndex.drawer - 1 }} onClick={handleBackdropClick} />}
  </div>
}

export default ResizableDrawer

ResizableDrawer.propTypes = {
  initialWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  anchor: PropTypes.oneOf(["left", "right", "top", "bottom"]),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  backdrop: PropTypes.bool,
  minWidthPercent: PropTypes.number,
  maxWidthPercent: PropTypes.number,
  children: PropTypes.node,
  onResize: PropTypes.func,
  closeOnBackdropClick: PropTypes.bool,
  onDraggingChange: PropTypes.func,
}
